import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { getResponseMessage } from '../../../../utils/utils';
import { getManufacturers, getSearchManufacturers } from '../../../../crud/manufacturer.crud';
import { Manufacturer } from '../../../../interfaces/manufacturer';

export const useGetManufacturer = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [manufacturers, setManufacturers] = useState<Manufacturer[] | null>([]);
  const [page, setPage] = useState({
    page: 1,
    perPage: 100,
    total: 0,
  });

  const fetch = async (params: {
    category_id?: number;
    page?: number;
    per_page?: number;
    text?: string;
  }): Promise<Manufacturer[]> => {
    setLoading(true);

    try {
      const { data } = await getManufacturers({
        ...params,
        text: params.text ? params.text.trim() : undefined,
        per_page: params.per_page ?? 100,
      });

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { page, per_page, total } = data;

      setPage({
        page,
        perPage: per_page,
        total,
      });

      setManufacturers(data.data);

      return data.data;
    } catch (e) {
      enqueueSnackbar(`${intl.formatMessage({ id: 'ERROR' })}: ${getResponseMessage(e)}`, {
        variant: 'error',
      });

      return [];
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  const loadMore = async (params?: { category_id?: number, text?: string; }): Promise<Manufacturer[]> => {
    if (loadingMore) return [];

    const nextPage = page.page + 1;

    // если всё уже загружено
    if ((manufacturers?.length || 0) >= page.total) {
      return [];
    }

    setLoadingMore(true);

    try {
      const { data } = await getManufacturers({
        ...params,
        page: nextPage,
        per_page: 100,
      });

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { page: currentPage, per_page, total } = data;

      setPage({
        page: currentPage,
        perPage: per_page,
        total,
      });

      setManufacturers(prev => [...(prev || []), ...data.data]);

      return data.data;
    } catch (e) {
      enqueueSnackbar(`${intl.formatMessage({ id: 'ERROR' })}: ${getResponseMessage(e)}`, {
        variant: 'error',
      });

      return [];
    } finally {
      setTimeout(() => {
        setLoadingMore(false);
      }, 100);
    }
  };

  const fetchSearch = async (params: {
    page?: number;
    per_page?: number;
    search: string;
    // manufacturer_ids?: string;
  }): Promise<Manufacturer[]> => {
    setLoading(true);

    try {
      const { data } = await getSearchManufacturers({
        ...params,
        per_page: params.per_page ?? 50,
      });

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { page, per_page, total } = data;

      setPage({
        page,
        perPage: per_page,
        total,
      });

      setManufacturers(data.data);

      return data.data;
    } catch (e) {
      enqueueSnackbar(`${intl.formatMessage({ id: 'ERROR' })}: ${getResponseMessage(e)}`, {
        variant: 'error',
      });

      return [];
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  const loadMoreSearch = async (params: {
    search: string;
    // manufacturer_ids?: string;
  }): Promise<Manufacturer[]> => {
    if (loadingMore) return [];

    const nextPage = page.page + 1;

    // если всё уже загружено
    if ((manufacturers?.length || 0) >= page.total) {
      return [];
    }

    setLoadingMore(true);

    try {
      const { data } = await getSearchManufacturers({
        ...params,
        page: nextPage,
        per_page: 50,
      });

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { page: currentPage, per_page, total } = data;

      setPage({
        page: currentPage,
        perPage: per_page,
        total,
      });

      setManufacturers(prev => [...(prev || []), ...data.data]);

      return data.data;
    } catch (e) {
      enqueueSnackbar(`${intl.formatMessage({ id: 'ERROR' })}: ${getResponseMessage(e)}`, {
        variant: 'error',
      });

      return [];
    } finally {
      setTimeout(() => {
        setLoadingMore(false);
      }, 100);
    }
  };

  return {
    fetch,
    loadMore,
    fetchSearch,
    loadMoreSearch,
    loading,
    loadingMore,
    manufacturers,
    page,
    clearManufacturers: () => setManufacturers([]),
  };
};
